import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import hpData from "../../../assets/files/hp.json";

const HPOSelector = ({
  selectedTerms = [],
  onSelectionChange,
  maxSelections = 10,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [internalSelectedTerms, setInternalSelectedTerms] = useState([]);

  // Parse HPO data and create searchable terms
  const hpoTerms = useMemo(() => {
    // Access nodes from the correct path in the HPO JSON structure
    const nodes = hpData?.graphs?.[0]?.nodes;

    if (!nodes) return [];

    return nodes
      .filter((node) => {
        // Filter for CLASS type nodes with labels, excluding the root "All" term
        return (
          node.type === "CLASS" &&
          node.lbl &&
          node.lbl !== "All" &&
          node.id &&
          node.id.includes("HP_")
        );
      })
      .map((node) => {
        // Extract HP ID from the URL (e.g., "http://purl.obolibrary.org/obo/HP_0000002" -> "HP:0000002")
        const hpId = node.id.split("/").pop().replace("_", ":");

        // Get synonyms if they exist
        const synonyms = node.meta?.synonyms?.map((syn) => syn.val) || [];

        // Get definition
        const definition = node.meta?.definition?.val || "";

        // Create searchable text from all relevant fields
        const searchableFields = [
          node.lbl,
          hpId,
          ...synonyms,
          definition,
        ].filter(Boolean); // Remove empty values

        return {
          id: hpId,
          label: node.lbl,
          definition,
          synonyms,
          searchText: searchableFields.join(" ").toLowerCase(),
        };
      })
      .filter((term) => term.id && term.label); // Ensure we have valid terms
  }, []);

  // Create a map for quick HPO ID lookup
  const hpoTermsMap = useMemo(() => {
    const map = new Map();
    hpoTerms.forEach((term) => {
      map.set(term.id, term);
    });
    return map;
  }, [hpoTerms]);

  // Sync internal state with prop changes - convert IDs to full term objects
  useEffect(() => {
    if (Array.isArray(selectedTerms) && selectedTerms.length > 0) {
      const firstTerm = selectedTerms[0];

      if (typeof firstTerm === "string") {
        // Convert ID strings to full term objects
        const fullTerms = selectedTerms
          .map((id) => hpoTermsMap.get(id))
          .filter(Boolean);
        setInternalSelectedTerms(fullTerms);
      } else {
        // Already full objects
        setInternalSelectedTerms(selectedTerms);
      }
    } else {
      setInternalSelectedTerms([]);
    }
  }, [selectedTerms, hpoTermsMap]);

  // Filter terms based on search
  const filteredTerms = useMemo(() => {
    if (!searchTerm.trim()) return [];

    const query = searchTerm.toLowerCase();
    const results = hpoTerms
      .filter((term) => {
        // Check if term matches search query and isn't already selected
        const matchesSearch = term.searchText.includes(query);
        const notSelected = !internalSelectedTerms.some(
          (selected) => selected.id === term.id
        );
        return matchesSearch && notSelected;
      })
      .slice(0, 20); // Limit results for performance

    return results;
  }, [searchTerm, hpoTerms, internalSelectedTerms]);

  // Handle term selection
  const handleTermSelect = (term) => {
    if (internalSelectedTerms.length >= maxSelections) return;

    const newSelectedTerms = [...internalSelectedTerms, term];
    setInternalSelectedTerms(newSelectedTerms);
    onSelectionChange(newSelectedTerms);
    setSearchTerm("");
  };

  // Handle term removal
  const handleTermRemove = (termToRemove) => {
    const newSelectedTerms = internalSelectedTerms.filter(
      (term) => term.id !== termToRemove.id
    );
    setInternalSelectedTerms(newSelectedTerms);
    onSelectionChange(newSelectedTerms);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="h6" sx={{ mb: 2, color: "secondary.main" }}>
        Phenotype Terms (HPO)
      </Typography>

      <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
        Search and select Human Phenotype Ontology terms that describe the
        clinical features ({hpoTerms.length} terms available)
      </Typography>

      {/* Search Input */}
      <TextField
        fullWidth
        placeholder="Search HPO terms (e.g., 'seizures', 'intellectual disability')"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
          ),
        }}
        sx={{
          mb: 2,
          "& .MuiOutlinedInput-root": {
            borderRadius: 2,
            "& fieldset": {
              borderColor: "#bdbdbd",
              borderWidth: 2,
            },
            "&:hover fieldset": {
              borderColor: "#757575",
              borderWidth: 2,
            },
            "&.Mui-focused fieldset": {
              borderColor: "#757575",
              borderWidth: 2,
            },
          },
        }}
      />

      {/* Search Results */}
      {searchTerm && (
        <Paper
          elevation={2}
          sx={{
            maxHeight: 300,
            overflow: "auto",
            mb: 2,
            border: "1px solid #e0e0e0",
          }}
        >
          {filteredTerms.length > 0 ? (
            <List dense>
              {filteredTerms.map((term) => (
                <ListItem key={term.id} disablePadding>
                  <ListItemButton
                    onClick={() => handleTermSelect(term)}
                    disabled={internalSelectedTerms.length >= maxSelections}
                    sx={{
                      "&:hover": {
                        backgroundColor: "rgba(66, 138, 174, 0.1)",
                      },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Typography variant="body2" fontWeight={500}>
                            {term.label}
                          </Typography>
                          <Chip
                            label={term.id}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontSize: "0.7rem",
                              height: 20,
                              "& .MuiChip-label": { px: 1 },
                            }}
                          />
                        </Box>
                      }
                      secondary={
                        term.definition && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              mt: 0.5,
                            }}
                          >
                            {term.definition}
                          </Typography>
                        )
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          ) : (
            <Box sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                No terms found matching &quot;{searchTerm}&quot;
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {/* Selected Terms */}
      {internalSelectedTerms.length > 0 && (
        <Box>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
            Selected Terms ({internalSelectedTerms.length}/{maxSelections}):
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {internalSelectedTerms.map((term) => (
              <Chip
                key={term.id}
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <span>{term.label}</span>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      ({term.id})
                    </Typography>
                  </Box>
                }
                onDelete={() => handleTermRemove(term)}
                sx={{
                  maxWidth: 300,
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                  border: "1px solid rgba(0, 0, 0, 0.23)",
                  "& .MuiChip-label": {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    pr: 1, // Add padding right to create space before delete icon
                  },
                  "& .MuiChip-deleteIcon": {
                    ml: 0.5, // Add margin left to the delete icon
                  },
                }}
              />
            ))}
          </Box>

          {internalSelectedTerms.length >= maxSelections && (
            <Typography
              variant="caption"
              color="warning.main"
              sx={{ mt: 1, display: "block" }}
            >
              Maximum number of terms selected
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default HPOSelector;
